import React, {ChangeEvent, useLayoutEffect, useRef} from 'react'
import {TwTextAreaLite} from '@components/common/base/textarea/containers'
import {Color, Paddings, Size} from '@components/common/base/textarea/types'
import {noDefault} from '@containers/hooks'

export enum ValidationState {
  Default = 'default',
  Success = 'success',
  Error = 'error'
}

export type TextareaLiteProps = Pick<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  | 'autoFocus'
  | 'placeholder'
  | 'value'
  | 'defaultValue'
  | 'name'
  | 'autoComplete'
> & {
  className?: string
  onChange?: (value: string, e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void
  disabled?: boolean
  validationState?: ValidationState
  inputStyle?: React.CSSProperties
  noBorder?: boolean
  paddings?: Paddings
  color?: Color
  noBackground?: boolean
  textSize?: Size
  /**
   * If true, the textarea will automatically adjust its height to fit the content
   */
  autoHeight?: boolean
  /**
   * Number of visible text lines by the default (also used as a minimum number of lines)
   */
  lines?: number
  /**
   * Maximum number of text lines (if autoHeight is enabled)
   */
  maxLines?: number
}

export const TextareaLite = (props: TextareaLiteProps) => {
  const {
    autoFocus,
    placeholder,
    value,
    defaultValue,
    name,
    autoComplete,
    className,
    validationState = ValidationState.Default,
    disabled,
    onChange,
    onPaste,
    inputStyle,
    noBorder,
    paddings = 'medium',
    color = 'primary',
    noBackground,
    textSize,
    autoHeight,
    lines = 2,
    maxLines
  } = props

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustHeight = () => {
    if (autoHeight && textareaRef.current) {
      const ta = textareaRef.current
      ta.style.height = 'auto'
      // const computed = window.getComputedStyle(ta)
      const lineHeight = 24 // parseFloat(computed.lineHeight) gives wrong value
      const minHeight = lines * lineHeight
      const maxHeight = maxLines ? maxLines * lineHeight : Infinity
      let newHeight = ta.scrollHeight
      newHeight = Math.max(newHeight, minHeight)
      newHeight = Math.min(newHeight, maxHeight)
      ta.style.minHeight = `${newHeight}px`
    }
  }

  useLayoutEffect(() => {
    adjustHeight()
  }, [value, autoHeight, lines, maxLines])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (autoHeight) {
      adjustHeight()
    }
    onChange?.(e.target.value, e)
  }

  return (
    <TwTextAreaLite
      ref={textareaRef}
      autoFocus={autoFocus}
      placeholder={placeholder}
      value={value}
      defaultValue={defaultValue}
      name={name}
      autoComplete={autoComplete}
      className={className}
      onChange={handleChange}
      onPaste={onPaste}
      onDragStart={noDefault}
      draggable={false}
      $disabled={disabled}
      $hasError={validationState === ValidationState.Error}
      $isSuccess={validationState === ValidationState.Success}
      style={inputStyle}
      $borders={!noBorder}
      $paddings={paddings}
      $color={color}
      $noBackground={noBackground}
      $textSize={textSize}
      $autoHeight={autoHeight}
    />
  )
}
